/**
 * Helpers compartidos por la lista y la ficha del logbook — Fase 6b.
 *
 * Concentran las decisiones de presentación derivadas (label de estado,
 * URL del avatar con fallback, formato de horas) para que ambas
 * pantallas se mantengan coherentes.
 */

import type {
  PilotStatsAggregate,
  PilotStatus,
  RecentMissionResult,
  Specialty,
} from '@51esc/shared';

/**
 * Convierte el id del avatar (p.ej. `"piloto5"`) en URL pública. Si no
 * hay avatar elegido (`null`/`undefined`), devuelve el fallback decidido
 * 2026-05-09: `/avatars/piloto1.png`. El piloto puede cambiarlo cuando
 * exista la edición de ficha (Fase futura).
 */
export function avatarUrl(avatarId: string | null | undefined): string {
  const id = avatarId ?? 'piloto1';
  return `/avatars/${id}.png`;
}

/** Label del estado del piloto en la tabla y la ficha. */
export function statusLabel(status: PilotStatus): string {
  return status === 'active' ? 'ACTIVO' : 'BAJA';
}

/**
 * Convierte horas decimales (3.5) en `HH:MM` (`03:30`). Mantiene el
 * formato del mockup. Las horas vienen redondeadas a 2 decimales del
 * backend; aquí volvemos a minutos para minimizar errores acumulados.
 */
export function formatHoursDecimal(hoursDecimal: number): string {
  const totalMinutes = Math.round(hoursDecimal * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Formato `HH:MM` desde segundos totales. Útil para stats absolutos. */
export function formatSecondsAsHm(sec: number): string {
  const totalMinutes = Math.round(sec / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** ISO timestamp → `dd/mm/yyyy`. Devuelve "—" si no es interpretable. */
export function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Etiqueta legible del resultado heurístico de una misión:
 *  - `success` → ÉXITO
 *  - `kia`     → BAJA
 *  - `unknown` → "—" (sin datos suficientes en el reporte).
 */
export function formatMissionResult(result: RecentMissionResult): string {
  if (result === 'success') return 'ÉXITO';
  if (result === 'kia') return 'BAJA';
  return '—';
}

/**
 * Etiqueta visible de la especialidad + flag `isAuto`. Si el piloto la
 * dejó en `auto`, comparamos sus killsAir y killsGround para resolver:
 *  - killsAir > killsGround → CAZA
 *  - killsGround > killsAir → ATAQUE
 *  - empate (incluido 0-0) → SIN DEFINIR
 *
 * El flag `isAuto` permite al frontend marcar visualmente "(auto)" para
 * distinguir cálculo dinámico de elección explícita del piloto.
 */
export function displaySpecialty(
  specialty: Specialty | undefined,
  stats: PilotStatsAggregate,
): { label: string; isAuto: boolean; isUnset: boolean } {
  if (!specialty) return { label: '—', isAuto: false, isUnset: true };
  if (specialty === 'caza') return { label: 'CAZA', isAuto: false, isUnset: false };
  if (specialty === 'ataque') return { label: 'ATAQUE', isAuto: false, isUnset: false };
  if (specialty === 'logistica') return { label: 'LOGÍSTICA', isAuto: false, isUnset: false };
  // 'auto' → resolver por kills
  if (stats.totalKillsAir > stats.totalKillsGround) {
    return { label: 'CAZA', isAuto: true, isUnset: false };
  }
  if (stats.totalKillsGround > stats.totalKillsAir) {
    return { label: 'ATAQUE', isAuto: true, isUnset: false };
  }
  return { label: 'SIN DEFINIR', isAuto: true, isUnset: false };
}

/** Escapa HTML para inyectar texto venido del backend de forma segura. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
